import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import KeyIcon from '@mui/icons-material/Key';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { dataGridHeaderSx } from '../../theme/themes';
import { useAuth } from '../../contexts/AuthContext';

interface DeviceApiKeyRow {
  id: string;
  deviceId: string;
  deviceName: string;
  businessNames: string[];
  label: string;
  keySuffix: string | null;
  dateCreated: string;
  lastUsedDate: string | null;
  revokedDate: string | null;
}

type StatusFilter = 'active' | 'revoked' | 'all';

// Every site's client data API keys in one place: who can read what, and a
// way to cut a key off. Issuing a key is done on the site's own page.
const DeviceApiKeyManagement: React.FC = () => {
  const auth = useAuth();
  const { mode } = useThemeMode();
  const accessToken = auth.tokens?.accessToken;
  const authHeader = { Authorization: `Bearer ${accessToken}` };

  const [rows, setRows] = useState<DeviceApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [revoking, setRevoking] = useState<DeviceApiKeyRow | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/device-api-keys', { headers: authHeader });
      if (!res.ok) throw new Error('Failed to load API keys');
      setRows(await res.json());
    } catch (err: any) {
      setError(err.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const revokeKey = async () => {
    if (!revoking) return;
    try {
      setActionLoading(true);
      setError(null);
      const res = await fetch(`/api/devices/${revoking.deviceId}/api-keys/${revoking.id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!res.ok) throw new Error('Failed to revoke API key');
      setRevokeOpen(false);
      await loadKeys();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke API key');
      setRevokeOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredRows = useMemo(
    () => rows.filter((r) => statusFilter === 'all' || (statusFilter === 'revoked') === !!r.revokedDate),
    [rows, statusFilter]
  );

  const columns: GridColDef[] = [
    {
      field: 'deviceName',
      headerName: 'Site',
      flex: 1,
      minWidth: 170,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component={RouterLink}
            to={`/devices/${params.row.deviceId}/events`}
            variant="body2"
            noWrap
            sx={{ display: 'block', color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}
          >
            {params.row.deviceName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {(params.row.businessNames as string[]).join(', ') || 'No business'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'label',
      headerName: 'Issued to',
      flex: 1,
      minWidth: 170,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>{params.row.label}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontFamily: 'monospace' }}>
            dmo_dev_…{params.row.keySuffix}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'dateCreated',
      headerName: 'Created',
      width: 110,
      valueGetter: (params) => new Date(params.row.dateCreated),
      renderCell: (params: GridRenderCellParams) => (
        <span title={new Date(params.row.dateCreated).toLocaleString()}>
          {new Date(params.row.dateCreated).toLocaleDateString()}
        </span>
      ),
    },
    {
      field: 'lastUsedDate',
      headerName: 'Last used',
      width: 110,
      valueGetter: (params) => (params.row.lastUsedDate ? new Date(params.row.lastUsedDate) : null),
      renderCell: (params: GridRenderCellParams) => params.row.lastUsedDate ? (
        <span title={new Date(params.row.lastUsedDate).toLocaleString()}>
          {new Date(params.row.lastUsedDate).toLocaleDateString()}
        </span>
      ) : 'Never',
    },
    {
      field: 'revokedDate',
      headerName: 'Status',
      width: 160,
      valueGetter: (params) => (params.row.revokedDate ? 'Revoked' : 'Active'),
      renderCell: (params: GridRenderCellParams) => params.row.revokedDate
        ? <Chip size="small" label={`Revoked ${new Date(params.row.revokedDate).toLocaleDateString()}`} />
        : <Chip size="small" color="success" label="Active" />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => params.row.revokedDate ? null : (
        <Button size="small" color="error" onClick={() => { setRevoking(params.row as DeviceApiKeyRow); setRevokeOpen(true); }}>Revoke</Button>
      ),
    },
  ];

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <KeyIcon sx={{ mr: 1 }} />
        <Typography variant="h5">Data API Keys</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Keys that let other systems read a site's container and number-plate sightings through the data API.
        A key is issued from its site's page (Devices → the site → API Keys).
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, value: StatusFilter | null) => value && setStatusFilter(value)}
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="revoked">Revoked</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Paper sx={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: 'deviceName', sort: 'asc' }] },
          }}
          rowHeight={60}
          disableRowSelectionOnClick
          sx={dataGridHeaderSx(mode)}
        />
      </Paper>

      <Dialog
        open={revokeOpen}
        onClose={() => !actionLoading && setRevokeOpen(false)}
        TransitionProps={{ onExited: () => setRevoking(null) }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revoke this key?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The key for <strong>{revoking?.deviceName}</strong> issued to <strong>{revoking?.label}</strong> (…{revoking?.keySuffix})
            will stop working straight away, and can't be brought back.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={revokeKey} disabled={actionLoading}>
            {actionLoading ? 'Revoking…' : 'Revoke key'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceApiKeyManagement;
